import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, catchError, filter, Observable, take, of} from 'rxjs';
import {map, tap,} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {LocalStorageService} from './local-storage.service';


export interface SubscriptionStatus {
  status: string;
  hasProFeatures: boolean;
  cancelAtPeriodEnd?: boolean;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  canManageSubscription?: boolean;
  /**
   * Optional: how the user's Pro access is sourced — 'SUBSCRIPTION' (their own,
   * wins if both apply), 'GROUP' (solely via a proEntitled group membership,
   * no billing account behind it), or 'NONE'. Absent on older backends; treat
   * absence exactly like today (never assume GROUP).
   */
  proSource?: 'SUBSCRIPTION' | 'GROUP' | 'NONE';
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private apiUrl = environment.apiURL;

  private hasProFeaturesSubject = new BehaviorSubject<boolean>(false);

  private statusSubject = new BehaviorSubject<SubscriptionStatus>({
    status: 'Free',
    hasProFeatures: false
  });
  status$ = this.statusSubject.asObservable();

  hasProFeatures$ = this.status$.pipe(map(s => s.hasProFeatures));

  private loadedSubject = new BehaviorSubject<boolean>(false);
  loaded$ = this.loadedSubject.asObservable();

  private loading = false;

  constructor(private http: HttpClient,  private storageService: LocalStorageService) {}

  /**
   * Initialize subscription status on app startup
   */
  init(): Observable<any> {
    const email = this.storageService.getItem('email');
    const token = this.storageService.getItem('token');
    if (!email || !token) {
      this.loadedSubject.next(true);
      return of(null);
    }
    return this.loadStatus(email);
  }


  loadStatus(email: string): Observable<any> {
    if (this.loading) {
      return this.loaded$.pipe(
        filter(loaded => loaded),
        take(1),
        map(() => ({
          hasProFeatures: this.hasProFeaturesSubject.value,
          status: this.statusSubject.value
        }))
      );
    }

    this.loading = true;

    return this.http.get<SubscriptionStatus>(`${this.apiUrl}/payment/status?email=${email}`).pipe(
      tap(response => {
        // this.hasProFeaturesSubject.next(response.hasProFeatures);
        this.statusSubject.next(response);
        this.loadedSubject.next(true);
        this.loading = false;
      }),
      catchError(error => {
        this.loadedSubject.next(true);
        this.loading = false;
        throw error;
      })
    );
  }

  get hasProFeatures(): boolean {
    return this.statusSubject.value.hasProFeatures;
  }

  get isLoaded(): boolean {
    return this.loadedSubject.value;
  }

  clear(): void {
    this.statusSubject.next({ status: 'Free', hasProFeatures: false });
    this.loadedSubject.next(false);
    this.loading = false;
  }
}
